import React from "react";
import { Calendar, Info } from "lucide-react";
import { shortDate, urgencyOf, urgencyColor } from "@/lib/dates";
import { cn } from "@/lib/utils";
import { RENEWAL_MODE, resolveRenewalDisplay } from "@/lib/renewalDisplay";

const ROWS = [
  { key: "judgment_entered", label: "Judgment Entered" },
  { key: "judgment_expires", label: "Judgment Expires" },
  { key: "renewal_deadline", label: "Renewal Deadline", renewalRelated: true },
  { key: "lien_renewal_window", label: "Lien Renewal Window", renewalRelated: true },
  { key: "next_query", label: "Next Recommended Query" },
];

const CriticalDatesCard = ({ criticalDates }) => {
  const dates = criticalDates?.dates || {};
  const stateLaw = criticalDates?.state_law;
  const state = criticalDates?.state;
  // Phase 9G — NO_PATHWAY states hide renewal-related rows entirely.
  const noPathway = resolveRenewalDisplay(criticalDates) === RENEWAL_MODE.NO_PATHWAY;
  return (
    <div className="bg-bg-secondary border border-border-default rounded-[12px] p-6" data-testid="critical-dates-card">
      <h3 className="text-white font-semibold text-[16px]">Critical Dates</h3>
      <div className="mt-4 space-y-3">
        {ROWS.map(({ key, label, renewalRelated }) => {
          if (renewalRelated && noPathway) return null;
          const d = dates[key];
          if (!d) return null;
          const urgency = urgencyOf(d.days_until);
          const color = urgencyColor(urgency);
          // Suppress critical pulse animation on renewal-related rows when
          // mode is ATTORNEY_REQUIRED too — the admin opted into a static
          // "attorney required" display, not a flashing alarm.
          const pulse = urgency === "critical" && !renewalRelated;
          return (
            <div
              key={key}
              className={cn(
                "flex items-center justify-between gap-3 text-[13px]",
                pulse && "animate-pulse-alert"
              )}
              data-testid={`critical-date-${key}`}
            >
              <div className="flex items-center gap-2 text-text-secondary">
                <Calendar size={14} style={{ color }} />
                <span>{label}</span>
              </div>
              <span
                className={cn(
                  "jea-mono text-[13px]",
                  urgency === "past" && "line-through"
                )}
                style={{ color }}
              >
                {shortDate(d.iso)}
              </span>
            </div>
          );
        })}
        {noPathway && (
          <div
            className="rounded-[8px] border border-border-default bg-bg-tertiary px-3 py-2 text-[12px] text-text-secondary"
            data-testid="critical-dates-no-pathway-note"
          >
            No renewal pathway available in this jurisdiction.
          </div>
        )}
      </div>
      {stateLaw && state && (
        <div className="mt-4 pt-4 border-t border-border-default text-[11px] text-text-muted flex items-start gap-2" data-testid="critical-dates-legal-note">
          <Info size={12} className="mt-0.5 text-text-secondary" />
          <span>
            Per {state} statute — judgments last {stateLaw.judgment_lifespan_years} years,
            liens last {stateLaw.lien_lifespan_years} years.
          </span>
        </div>
      )}
    </div>
  );
};

export default CriticalDatesCard;
